import { Paper, Stack, Text, Badge, Alert, Button, Group, List, Title } from '@mantine/core';
import {
  IconFileTypeCsv,
  IconAlertCircle,
  IconAlertTriangle,
  IconCalendar,
  IconClockHour4,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import type { CsvPreviewResult } from '../../../../utils/csvPreview';

const MONTH_NAMES = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
];

interface CsvPreviewProps {
  preview: CsvPreviewResult;
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  /** Whether a tariff has been selected - required for import */
  tariffSelected?: boolean;
}

export function CsvPreview({ preview, fileName, onConfirm, onCancel, loading, tariffSelected = true }: CsvPreviewProps) {
  const isFileValid = preview.isValid && preview.errors.length === 0;
  const canImport = isFileValid && tariffSelected;

  return (
    <Paper p="md" withBorder radius="md" shadow="sm">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconFileTypeCsv size={24} style={{ color: 'var(--mantine-color-blue-6)' }} />
            <div>
              <Title order={4} size="h5">
                Podgląd pliku CSV
              </Title>
              <Text size="sm" c="dimmed">
                {fileName}
              </Text>
            </div>
          </Group>
          <Badge
            size="lg"
            color={isFileValid ? (tariffSelected ? 'green' : 'yellow') : 'red'}
            variant="light"
            leftSection={isFileValid ? <IconCheck size={16} /> : <IconX size={16} />}
          >
            {isFileValid
              ? (tariffSelected ? 'Gotowy do importu' : 'Wybierz taryfę')
              : 'Nieprawidłowy plik'}
          </Badge>
        </Group>

        {/* Errors */}
        {preview.errors.length > 0 && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertCircle size={18} />}
            title="Błędy walidacji"
          >
            <List size="sm" spacing="xs">
              {preview.errors.map((error, index) => (
                <List.Item key={index}>{error}</List.Item>
              ))}
            </List>
          </Alert>
        )}

        {/* Tariff Not Selected Warning */}
        {isFileValid && !tariffSelected && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            title="Wymagana taryfa"
          >
            <Text size="sm">
              Wybierz taryfę energii powyżej, aby móc zaimportować dane.
            </Text>
          </Alert>
        )}

        {/* Warnings */}
        {preview.warnings.length > 0 && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            title="Ostrzeżenia"
          >
            <List size="sm" spacing="xs">
              {preview.warnings.slice(0, 5).map((warning, index) => (
                <List.Item key={index}>{warning}</List.Item>
              ))}
              {preview.warnings.length > 5 && (
                <Text size="xs" c="dimmed" mt="xs">
                  ... i {preview.warnings.length - 5} więcej ostrzeżeń
                </Text>
              )}
            </List>
          </Alert>
        )}

        {/* Valid file preview - show when file is valid (regardless of tariff selection) */}
        {isFileValid && (
          <Stack gap="sm">
            {/* Date Range */}
            {preview.dateRange && (
              <Group gap="xs">
                <IconCalendar size={18} style={{ color: 'var(--mantine-color-dimmed)' }} />
                <Text size="sm">
                  <strong>Zakres dat:</strong> {preview.dateRange.from} → {preview.dateRange.to}
                </Text>
              </Group>
            )}

            {/* Total Rows */}
            <Group gap="xs">
              <IconClockHour4 size={18} style={{ color: 'var(--mantine-color-dimmed)' }} />
              <Text size="sm">
                <strong>Liczba wierszy:</strong>{' '}
                {preview.totalRows.toLocaleString('pl-PL')} godzin
              </Text>
            </Group>

            {/* Detected Years */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                Wykryte lata:
              </Text>
              <Group gap="xs">
                {preview.years.map((year) => (
                  <Badge key={year} size="lg" color="blue" variant="light">
                    {year}
                  </Badge>
                ))}
              </Group>
            </div>

            {/* Months Breakdown */}
            {preview.months.length > 0 && (
              <div>
                <Text size="sm" fw={500} mb="xs">
                  Miesiące do zaimportowania:
                </Text>
                <Group gap="xs">
                  {preview.months.map((m) => (
                    <Badge
                      key={`${m.year}-${m.month}`}
                      size="md"
                      color="green"
                      variant="light"
                    >
                      {MONTH_NAMES[m.month - 1]} {m.year} ({m.rowCount.toLocaleString('pl-PL')}{' '}
                      godz.)
                    </Badge>
                  ))}
                </Group>
              </div>
            )}
          </Stack>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" onClick={onCancel} disabled={loading}>
            Anuluj
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!canImport || loading}
            loading={loading}
            color="blue"
          >
            Importuj dane
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
